import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Send, MessageCircle, RefreshCw } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const httpAuth = () => ({ Authorization: `Bearer ${localStorage.getItem("wt_token")}` });
const POLL_MS = 15000;

export default function AdminLaunchChats() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [threads, setThreads] = useState(null);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => {
    if (user === null || (user && user.role !== "admin")) navigate("/login");
  }, [user, navigate]);

  const loadThreads = async () => {
    try {
      const r = await axios.get(`${API}/admin/launch/chats`, { headers: httpAuth() });
      setThreads(r.data.items || []);
    } catch { setThreads([]); }
  };

  const loadMessages = async (token) => {
    try {
      const r = await axios.get(`${API}/launch/chat/${token}`, { headers: httpAuth() });
      setMessages(r.data.items || []);
    } catch { setMessages([]); }
  };

  useEffect(() => { if (user?.role === "admin") loadThreads(); }, [user]);

  useEffect(() => {
    if (!active) return;
    loadMessages(active.public_token);
    pollRef.current = setInterval(() => loadMessages(active.public_token), POLL_MS);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.public_token]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    if (!active || !text.trim() || sending) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/admin/launch/chat/${active.public_token}`,
        { text: text.trim() }, { headers: httpAuth() });
      const msg = r.data?.message;
      if (msg) setMessages((prev) => [...prev, msg]);
      setText("");
      loadThreads();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-launch-chats">
      <div className="max-w-7xl mx-auto px-4 py-6">
        <Link to="/admin"
              className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-2"
              data-testid="launch-chats-back">
          <ArrowLeft className="w-4 h-4" /> Back to Admin
        </Link>
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold text-slate-900">Launch chats</h1>
          <button onClick={loadThreads}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm ring-1 ring-slate-200 bg-white hover:bg-slate-50">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>

        <div className="grid grid-cols-12 gap-4 h-[calc(100vh-180px)]">
          {/* Threads list */}
          <div className="col-span-4 bg-white ring-1 ring-slate-200 rounded-xl overflow-y-auto">
            {threads === null && (
              <div className="p-8 text-center text-slate-400">
                <Loader2 className="w-5 h-5 animate-spin inline-block mr-1" /> Loading…
              </div>
            )}
            {threads?.length === 0 && (
              <div className="p-8 text-center text-slate-400 text-sm">
                No launch chats yet.
              </div>
            )}
            {threads?.map((t) => (
              <button key={t.public_token}
                      onClick={() => setActive(t)}
                      className={`w-full text-left p-3 border-b border-slate-100 hover:bg-slate-50 ${
                        active?.public_token === t.public_token ? "bg-orange-50" : ""}`}
                      data-testid={`launch-chat-thread-${t.public_token}`}>
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-slate-900 text-sm truncate">
                    {t.business_name || t.public_token}
                  </div>
                  <span className="text-[10px] text-slate-400">
                    {new Date(t.last_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </span>
                </div>
                <div className="text-xs text-slate-500 mt-0.5 line-clamp-1">
                  <span className="font-semibold">
                    {t.last_sender === "applicant" ? "Client" : "You"}:
                  </span>{" "}
                  {t.last_text}
                </div>
                <div className="text-[10px] text-slate-400 mt-1">
                  {t.message_count} messages · {t.applicant_message_count} from client
                </div>
              </button>
            ))}
          </div>

          {/* Active chat */}
          <div className="col-span-8 bg-white ring-1 ring-slate-200 rounded-xl flex flex-col">
            {!active ? (
              <div className="flex-1 flex items-center justify-center text-slate-400">
                <MessageCircle className="w-6 h-6 mr-2" /> Pick a thread to reply
              </div>
            ) : (
              <>
                <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Chat with</div>
                    <div className="font-bold text-slate-900">{active.business_name || active.public_token}</div>
                  </div>
                  <Link to={`/launch/status/${active.public_token}`}
                         target="_blank"
                         className="text-xs text-sky-600 hover:underline">
                    Open portal ↗
                  </Link>
                </div>
                <div ref={scrollRef}
                      className="flex-1 overflow-y-auto p-3 space-y-2 bg-slate-50"
                      data-testid="admin-launch-chat-messages">
                  {messages.map((m) => (
                    <div key={m.id}
                          className={`max-w-[75%] rounded-xl px-3 py-2 text-sm ${
                            m.sender === "team"
                              ? "ml-auto bg-slate-900 text-white rounded-br-none"
                              : "bg-white ring-1 ring-slate-200 text-slate-800 rounded-bl-none"}`}
                          data-testid={`admin-launch-chat-msg-${m.sender}`}>
                      <div className="whitespace-pre-line">{m.text}</div>
                      <div className={`text-[10px] mt-1 ${m.sender === "team" ? "text-slate-400" : "text-slate-400"}`}>
                        {new Date(m.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        {m.sender === "team" && m.sender_email ? ` · ${m.sender_email}` : ""}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="border-t border-slate-100 p-2 flex items-end gap-2">
                  <textarea value={text}
                             onChange={(e) => setText(e.target.value)}
                             onKeyDown={(e) => {
                               if (e.key === "Enter" && !e.shiftKey) {
                                 e.preventDefault(); send();
                               }
                             }}
                             rows={2}
                             placeholder="Reply to the applicant…"
                             className="flex-1 resize-none text-sm rounded-lg ring-1 ring-slate-200 px-3 py-2 focus:ring-slate-400 outline-none max-h-40"
                             data-testid="admin-launch-chat-input" />
                  <button onClick={send}
                          disabled={sending || !text.trim()}
                          className="p-2.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white disabled:opacity-40"
                          data-testid="admin-launch-chat-send">
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
